import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Laptop, Monitor, RefreshCw, Search } from "lucide-react";

import { NGROK } from "../../../APIs";
import useUserStore from "../../../services/userStore";
import useCloudSyncStore from "../../../services/useCloudSyncStore";
import useNotificationStore from "../../../services/notificationStore";
import { myLocalStorage } from "../../../components/StorageHelper";
import { Card, CardContent } from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { CloudCredentialExpiredSyncButton } from "../../../components/v2components/CloudCredentialExpired";
import { useCloudCredentialExpiry } from "../../../services/useCloudCredentialStore";
import { triggerCloudSync } from "../../../triggerCloudSync";
import AzureDeviceItem from "./Components/AzureDeviceItem";
import "../../../components/v2components/Cloud/CloudStatus.css";

const AzureDevices = () => {
  const [selectedTenantName, setSelectedTenantName] = useState(null);
  const userData = useUserStore((state) => state.user);
  const [tenantList, setTenantList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [devices, setDevices] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const syncStatus = useCloudSyncStore((state) => state.syncStatus);
  const { azureConfig } = useNotificationStore.getState();

  const demandSync = async () => {
    try {
      await triggerCloudSync(
        "azure",
        "/api/azure-sync/onDemand",
        selectedTenantName,
      );
      await fetchAzureDevices(true);
    } catch (error) {
      console.error("Azure Sync failed", error);
    }
  };

  const handleTenantChange = (value) => {
    const tenant = tenantList.find((item) => item.tenantName === value);
    if (tenant) {
      setSelectedTenantName(tenant.tenantName);
      myLocalStorage.setItem("latestTenant", tenant);
    }
  };

  const fetchAzureDevices = async (isFirst = false) => {
    if (!selectedTenantName) return;

    try {
      if (isFirst) {
        setLoading(true);
      }
      const response = await axios.get(
        `${NGROK}/api/azure/v1/devices?tenantName=${selectedTenantName}`,
      );
      setDevices(Array.isArray(response?.data) ? response.data : []);
    } catch (error) {
      console.error("Failed to fetch Azure devices", error);
      setDevices([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (tenantList.length === 1) {
      setSelectedTenantName(tenantList[0].tenantName);
      return;
    }

    if (tenantList.length > 1) {
      const latestTenantName =
        myLocalStorage.getItem("latestTenant")?.tenantName;

      const tenant = tenantList.find(
        (item) => item.tenantName === latestTenantName,
      );
      setSelectedTenantName(tenant ? tenant.tenantName : tenantList[0].tenantName);
    }
  }, [tenantList]);

  useEffect(() => {
    const fetchTenants = async () => {
      try {
        const response = await axios.get(`${NGROK}/api/get-all-domains`);
        setTenantList(Array.isArray(response.data) ? response.data : []);
      } catch (error) {
        console.error("Failed to fetch tenants", error);
        setTenantList([]);
      }
    };

    fetchTenants();
  }, [userData]);

  useEffect(() => {
    if (!selectedTenantName) return undefined;

    fetchAzureDevices(true);
    const interval = setInterval(() => {
      fetchAzureDevices();
    }, 5000);

    return () => clearInterval(interval);
  }, [selectedTenantName]);

  const filteredDevices = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return devices;

    return devices.filter((device) => {
      const searchable = [
        device.displayName,
        device.operatingSystem,
        device.operatingSystemVersion,
        device.id,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return searchable.includes(query);
    });
  }, [devices, searchQuery]);

  const windowsCount = devices.filter((device) =>
    (device.operatingSystem || "").toLowerCase().includes("windows"),
  ).length;
  const otherOsCount = Math.max(devices.length - windowsCount, 0);
  const isSyncing = Boolean(syncStatus?.azure);
  const { expired: credentialsExpired, reason: credentialsExpiredReason } = useCloudCredentialExpiry(
    "azure",
    selectedTenantName || undefined,
  );

  return (
    <div className="h-full flex flex-col bg-background overflow-auto">
      <div className="p-6 space-y-6">
        <Card className="cloud-card-surface rounded-2xl">
          <CardContent className="p-3 flex flex-col lg:flex-row lg:items-center justify-between gap-5">
            <div className="flex items-center gap-4 min-w-0">
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Monitor className="w-6 h-6 text-primary" />
              </div>
              <div className="min-w-0">
                <h1 className="text-xl font-semibold text-foreground">Azure Devices</h1>
                <p className="text-sm text-muted-foreground">
                  Review devices discovered from Microsoft Azure.
                </p>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row sm:items-end gap-3 w-full lg:w-auto">
              <div className="flex flex-col gap-1.5 min-w-[220px]">
                <div className="text-[11px] font-medium text-muted-foreground/70 uppercase tracking-wider px-1">
                  Tenant
                </div>
                <select
                  value={selectedTenantName || ""}
                  onChange={(event) => handleTenantChange(event.target.value)}
                  className="h-[43px] w-full rounded-xl bg-card border border-border/50 px-4 py-2.5 text-[14px] font-medium text-foreground shadow-sm outline-none transition-colors hover:border-sky-500/30 focus:border-sky-500/40"
                  data-testid="select-azure-devices-tenant"
                >
                  {tenantList.length === 0 && <option value="">No tenants</option>}
                  {tenantList.map((tenant) => (
                    <option key={tenant.tenantName} value={tenant.tenantName}>
                      {tenant.tenantName}
                    </option>
                  ))}
                </select>
              </div>

              {credentialsExpired ? (
                <CloudCredentialExpiredSyncButton
                  reason={credentialsExpiredReason}
                  className="px-6 py-2.5"
                  testId="button-sync-azure-devices"
                />
              ) : (
                <Button
                  onClick={demandSync}
                  disabled={!azureConfig || !selectedTenantName || isSyncing}
                  className="px-6 py-2.5 rounded-xl font-bold shadow-md shadow-primary/20"
                  data-testid="button-sync-azure-devices"
                >
                  <RefreshCw className={`w-4 h-4 ${isSyncing ? "animate-spin" : ""}`} />
                  {isSyncing ? "Syncing..." : "Sync"}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Card className="cloud-card-surface rounded-2xl">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                <Laptop className="w-5 h-5 text-primary" />
              </div>
              <div>
                <div className="text-2xl font-semibold text-foreground">{devices.length}</div>
                <div className="text-xs text-muted-foreground">Total devices</div>
              </div>
            </CardContent>
          </Card>
          <Card className="cloud-card-surface rounded-2xl">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-blue-500/10 flex items-center justify-center">
                <Monitor className="w-5 h-5 text-blue-500" />
              </div>
              <div>
                <div className="text-2xl font-semibold text-foreground">{windowsCount}</div>
                <div className="text-xs text-muted-foreground">Windows devices</div>
              </div>
            </CardContent>
          </Card>
          <Card className="cloud-card-surface rounded-2xl">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-muted flex items-center justify-center">
                <Laptop className="w-5 h-5 text-muted-foreground" />
              </div>
              <div>
                <div className="text-2xl font-semibold text-foreground">{otherOsCount}</div>
                <div className="text-xs text-muted-foreground">Other OS</div>
              </div>
            </CardContent>
          </Card>
        </div>

        <Card className="cloud-card-surface rounded-2xl">
          <CardContent className="p-3 flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search devices by name, OS, version, or id..."
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                className="pl-10 h-11 rounded-xl bg-card border-border/50 shadow-sm focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:border-border/50"
                data-testid="input-search-azure-devices"
              />
            </div>
            <div className="text-sm text-muted-foreground">
              Showing <span className="font-medium text-foreground">{filteredDevices.length}</span> of{" "}
              <span className="font-medium text-foreground">{devices.length}</span> devices
            </div>
          </CardContent>
        </Card>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {Array.from({ length: 6 }).map((_, index) => (
              <Card key={index} className="cloud-card-surface rounded-2xl">
                <CardContent className="p-5 space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="w-12 h-12 rounded-xl bg-muted animate-pulse" />
                    <div className="h-6 w-20 rounded-md bg-muted animate-pulse" />
                  </div>
                  <div className="h-4 w-2/3 rounded-md bg-muted animate-pulse" />
                  <div className="space-y-2">
                    <div className="h-3 w-full rounded-md bg-muted animate-pulse" />
                    <div className="h-3 w-5/6 rounded-md bg-muted animate-pulse" />
                    <div className="h-3 w-4/6 rounded-md bg-muted animate-pulse" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : filteredDevices.length === 0 ? (
          <Card className="cloud-card-surface border-dashed rounded-2xl">
            <CardContent className="flex flex-col items-center justify-center py-12 text-center">
              <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
                <Monitor className="w-8 h-8 text-muted-foreground" />
              </div>
              <h3 className="text-lg font-semibold mb-2">
                {searchQuery ? "No Devices Found" : "No Devices Available"}
              </h3>
              <p className="text-sm text-muted-foreground max-w-md">
                {searchQuery
                  ? "No Azure devices match your search. Try adjusting your search terms."
                  : "No Azure devices are available for the selected tenant right now."}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {filteredDevices.map((device, index) => (
              <AzureDeviceItem key={device.id || device.deviceId || index} device={device} />
            ))}
          </div>
        )}

      </div>
    </div>
  );
};

export default AzureDevices;
